// Pure, dependency-free conversions between the stored `{token}` template
// string and the segment/Plate representations the editor UI works with.
// Token order/names MUST match EVENT_NAME_TEMPLATE_VARIABLES in
// apps/scheduling/services/event_name_template.py.

export interface EventNameToken {
  name: string;
  label: string;
}

export const EVENT_NAME_TOKENS: readonly EventNameToken[] = [
  { name: 'booking_type', label: 'Booking type' },
  { name: 'invitee', label: 'Invitee' },
  { name: 'host', label: 'Host' },
  { name: 'location', label: 'Location' },
] as const;

export const EVENT_NAME_TOKEN_NAMES: ReadonlySet<string> = new Set(
  EVENT_NAME_TOKENS.map(t => t.name)
);

export type TemplateSegment =
  | { type: 'text'; text: string }
  | { type: 'token'; name: string };

const TOKEN_PATTERN = /\{(\w+)\}/g;

export function parseTemplate(template: string): TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  let lastIndex = 0;
  let pendingText = '';

  for (const match of template.matchAll(TOKEN_PATTERN)) {
    const [raw, name] = match;
    const matchIndex = match.index ?? 0;
    pendingText += template.slice(lastIndex, matchIndex);
    lastIndex = matchIndex + raw.length;

    if (EVENT_NAME_TOKEN_NAMES.has(name)) {
      if (pendingText) {
        segments.push({ type: 'text', text: pendingText });
        pendingText = '';
      }
      segments.push({ type: 'token', name });
    } else {
      pendingText += raw;
    }
  }

  pendingText += template.slice(lastIndex);
  if (pendingText) {
    segments.push({ type: 'text', text: pendingText });
  }

  return segments;
}

export function serializeSegments(segments: TemplateSegment[]): string {
  return segments
    .map(seg => (seg.type === 'token' ? `{${seg.name}}` : seg.text))
    .join('');
}

interface TokenNode {
  type: 'token';
  tokenName: string;
  children: { text: string }[];
}

interface TextLeaf {
  text: string;
}

type PlateChild = TokenNode | TextLeaf;

function isTokenNode(node: unknown): node is TokenNode {
  return (
    typeof node === 'object' &&
    node !== null &&
    'type' in node &&
    (node as { type: unknown }).type === 'token'
  );
}

function isTextLeaf(node: unknown): node is TextLeaf {
  return (
    typeof node === 'object' &&
    node !== null &&
    'text' in node &&
    typeof (node as { text: unknown }).text === 'string'
  );
}

export function templateToPlateValue(template: string): unknown[] {
  const segments = parseTemplate(template);
  const children: PlateChild[] = [];

  for (const segment of segments) {
    if (segment.type === 'token') {
      if (children.length === 0 || isTokenNode(children[children.length - 1])) {
        children.push({ text: '' });
      }
      children.push({
        type: 'token',
        tokenName: segment.name,
        children: [{ text: '' }],
      });
    } else {
      children.push({ text: segment.text });
    }
  }

  if (children.length === 0 || isTokenNode(children[children.length - 1])) {
    children.push({ text: '' });
  }

  return [{ type: 'p', children }];
}

export function plateValueToTemplate(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }

  const firstParagraph = value[0];
  if (
    typeof firstParagraph !== 'object' ||
    firstParagraph === null ||
    !('children' in firstParagraph) ||
    !Array.isArray((firstParagraph as { children: unknown }).children)
  ) {
    return '';
  }

  const { children } = firstParagraph as { children: unknown[] };
  let result = '';

  for (const node of children) {
    if (isTokenNode(node)) {
      result += `{${node.tokenName}}`;
    } else if (isTextLeaf(node)) {
      result += node.text;
    }
  }

  return result;
}
